import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fab,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChecklistIcon from '@mui/icons-material/Checklist';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { useNavigate } from 'react-router-dom';
import VisualizationGuide from './VisualizationGuide';

const ONBOARDING_STORAGE_KEY = 'learnHub.onboarding.v1';

const onboardingItems = [
  { id: 'dashboard', label: 'Open Dashboard', path: '/dashboard' },
  { id: 'financials', label: 'Review Financial Statements', path: '/financial-statements' },
  { id: 'compare', label: 'Compare Two Companies', path: '/company-comparison' },
  { id: 'watchlist', label: 'Save Tickers to Watchlist', path: '/watchlist' },
  { id: 'alerts', label: 'Set One Price Alert', path: '/alerts' },
];

const quickActions = [
  { label: 'Open Screener', path: '/screener' },
  { label: 'Open Metrics', path: '/metric-table' },
  { label: 'Explore Crypto', path: '/crypto-analysis' },
  { label: 'Check Fed Rates', path: '/fed-rates' },
];

const LearnHub = () => {
  const navigate = useNavigate();
  const [openPanel, setOpenPanel] = useState(false);
  const [completed, setCompleted] = useState({});

  useEffect(() => {
    try {
      const saved = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          setCompleted(parsed);
        }
      }
    } catch (error) {
      console.warn('Failed to load onboarding progress:', error);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(completed));
  }, [completed]);

  const progress = useMemo(() => {
    const doneCount = onboardingItems.reduce((count, item) => count + (completed[item.id] ? 1 : 0), 0);
    const percent = Math.round((doneCount / onboardingItems.length) * 100);
    return { doneCount, percent };
  }, [completed]);

  const handleChecklistToggle = (id) => {
    setCompleted((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleChecklistAction = (item) => {
    setCompleted((prev) => ({ ...prev, [item.id]: true }));
    setOpenPanel(false);
    navigate(item.path);
  };

  const handleQuickAction = (path) => {
    setOpenPanel(false);
    navigate(path);
  };

  const handleResetProgress = () => {
    setCompleted({});
  };

  const handleSurpriseMe = () => {
    const randomAction = quickActions[Math.floor(Math.random() * quickActions.length)];
    handleQuickAction(randomAction.path);
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ p: { xs: 0, md: 0 } }}>
        <VisualizationGuide />
      </Box>

      <Fab
        color="primary"
        variant="extended"
        onClick={() => setOpenPanel(true)}
        sx={{
          position: 'fixed',
          right: { xs: 16, md: 28 },
          bottom: { xs: 16, md: 28 },
          zIndex: 1300,
          boxShadow: 6,
          px: 2,
        }}
      >
        <RocketLaunchIcon sx={{ mr: 1 }} />
        Quick Start
      </Fab>

      <Dialog
        open={openPanel}
        onClose={() => setOpenPanel(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ChecklistIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Interactive Onboarding
            </Typography>
          </Box>
          <Chip label={`${progress.doneCount}/${onboardingItems.length} done`} color="primary" size="small" />
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Use this panel as your launchpad. Complete tasks, track progress, and jump directly to each workflow.
          </Typography>

          <LinearProgress
            variant="determinate"
            value={progress.percent}
            sx={{ height: 8, borderRadius: 999, mb: 2 }}
          />

          <Stack spacing={1.25} sx={{ mb: 3 }}>
            {onboardingItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox
                    checked={Boolean(completed[item.id])}
                    onChange={() => handleChecklistToggle(item.id)}
                    size="small"
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                </Box>

                <Button size="small" onClick={() => handleChecklistAction(item)}>
                  Go
                </Button>
              </Box>
            ))}
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {quickActions.map((action) => (
              <Button
                key={action.path}
                variant="outlined"
                size="small"
                onClick={() => handleQuickAction(action.path)}
              >
                {action.label}
              </Button>
            ))}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleResetProgress}>Reset Progress</Button>
          <Button startIcon={<TravelExploreIcon />} onClick={handleSurpriseMe}>
            Surprise Me
          </Button>
          <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={() => setOpenPanel(false)}>
            Continue Exploring
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearnHub;
